'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import gsap from 'gsap';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { homeAssetSrc } from './homeAssetSrc';
import {
  bindOnceTextReveal,
  TEXT_REVEAL_FULL,
  TEXT_REVEAL_SUBTLE,
} from './scrollBeat';
import v3 from './v3.module.css';
import styles from './RingMeasuresSection.module.css';

const MD_MAX_PX = 992;
const DESKTOP_MQ = `(min-width: ${MD_MAX_PX}px)`;
const MOTION_OK_MQ = '(prefers-reduced-motion: no-preference)';
const AUTOPLAY_S = 4;

const canAutoplay = (): boolean =>
  window.matchMedia(DESKTOP_MQ).matches &&
  window.matchMedia(MOTION_OK_MQ).matches;

const measuresImg = (name: string, variant: 'full' | 'min') =>
  homeAssetSrc(`web_v2/home-v4/pro-measures/v2/${variant}/${name}.png`);

const CARD_COUNT = 5;

export const RingMeasuresSection = ({
  'aria-label': ariaLabel,
}: {
  'aria-label'?: string;
}) => {
  const t = useTranslations('homepage');
  const outerRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const subtitleRef = useRef<HTMLParagraphElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const tabListRef = useRef<HTMLDivElement | null>(null);
  const progressRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const progressTween = useRef<gsap.core.Tween | null>(null);
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAutoplayPausedRef = useRef(false);

  /* Keys are spelled out so check-i18n-keys / the payload manifest can see
     them — a computed `t(\`…${id}.title\`)` is invisible to both. */
  const cards = useMemo(
    () => [
      {
        id: 'activity' as const,
        imageSrc: measuresImg('movement', 'full'),
        imageSrcMobile: measuresImg('movement', 'min'),
        tab: t('homepage.ringMeasures.tabs.activity'),
        title: t('homepage.ringMeasures.cards.activity.title'),
        description: t('homepage.ringMeasures.cards.activity.description'),
        imageAlt: t('homepage.ringMeasures.cards.activity.imageAlt'),
      },
      {
        id: 'sleep' as const,
        imageSrc: measuresImg('sleep', 'full'),
        imageSrcMobile: measuresImg('sleep', 'min'),
        tab: t('homepage.ringMeasures.tabs.sleep'),
        title: t('homepage.ringMeasures.cards.sleep.title'),
        description: t('homepage.ringMeasures.cards.sleep.description'),
        imageAlt: t('homepage.ringMeasures.cards.sleep.imageAlt'),
      },
      {
        id: 'workouts' as const,
        imageSrc: measuresImg('workout', 'full'),
        imageSrcMobile: measuresImg('workout', 'min'),
        tab: t('homepage.ringMeasures.tabs.workouts'),
        title: t('homepage.ringMeasures.cards.workouts.title'),
        description: t('homepage.ringMeasures.cards.workouts.description'),
        imageAlt: t('homepage.ringMeasures.cards.workouts.imageAlt'),
      },
      {
        id: 'recovery' as const,
        imageSrc: measuresImg('recovery', 'full'),
        imageSrcMobile: measuresImg('recovery', 'min'),
        tab: t('homepage.ringMeasures.tabs.recovery'),
        title: t('homepage.ringMeasures.cards.recovery.title'),
        description: t('homepage.ringMeasures.cards.recovery.description'),
        imageAlt: t('homepage.ringMeasures.cards.recovery.imageAlt'),
      },
      {
        id: 'cycle' as const,
        imageSrc: measuresImg('cycles', 'full'),
        imageSrcMobile: measuresImg('cycles', 'min'),
        tab: t('homepage.ringMeasures.tabs.cycle'),
        title: t('homepage.ringMeasures.cards.cycle.title'),
        description: t('homepage.ringMeasures.cards.cycle.description'),
        imageAlt: t('homepage.ringMeasures.cards.cycle.imageAlt'),
      },
    ],
    [t],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    containScroll: false,
    loop: true,
    breakpoints: {
      [`(max-width: ${MD_MAX_PX - 1}px)`]: {
        align: 'start',
        containScroll: false,
        loop: true,
      },
    },
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(0);
  selectedIndexRef.current = selectedIndex;

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const syncTabHighlight = useCallback(() => {
    const list = tabListRef.current;
    const tab = tabRefs.current[selectedIndex];
    if (!list || !tab || list.offsetParent === null) return;
    const listRect = list.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    list.style.setProperty(
      '--rm-tab-x',
      `${Math.round(tabRect.left - listRect.left)}px`,
    );
    list.style.setProperty('--rm-tab-w', `${Math.round(tabRect.width)}px`);
  }, [selectedIndex]);

  useIsomorphicLayoutEffect(() => {
    syncTabHighlight();
  }, [syncTabHighlight]);

  useEffect(() => {
    window.addEventListener('resize', syncTabHighlight);
    return () => window.removeEventListener('resize', syncTabHighlight);
  }, [syncTabHighlight]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const killProgressTween = useCallback(() => {
    progressTween.current?.kill();
    progressTween.current = null;
  }, []);

  const clearAutoplayTimer = useCallback(() => {
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);

  const resetBars = useCallback((fillActive: boolean, activeIndex: number) => {
    progressRefs.current.forEach((el, i) => {
      if (!el) return;
      gsap.set(el, { width: fillActive && i === activeIndex ? '100%' : '0%' });
    });
  }, []);

  const stopAutoplay = useCallback(() => {
    clearAutoplayTimer();
    killProgressTween();
  }, [clearAutoplayTimer, killProgressTween]);

  const startAutoplay = useCallback(
    (activeIndex: number) => {
      if (!emblaApi || isAutoplayPausedRef.current || !canAutoplay()) {
        resetBars(true, activeIndex);
        return;
      }

      clearAutoplayTimer();
      killProgressTween();
      resetBars(false, activeIndex);

      const activeEl = progressRefs.current[activeIndex];
      if (activeEl) {
        gsap.set(activeEl, { width: '0%' });
        progressTween.current = gsap.to(activeEl, {
          width: '100%',
          duration: AUTOPLAY_S,
          ease: 'none',
        });
      }

      autoplayTimerRef.current = setTimeout(() => {
        emblaApi.scrollNext();
      }, AUTOPLAY_S * 1000);
    },
    [emblaApi, clearAutoplayTimer, killProgressTween, resetBars],
  );

  useEffect(() => {
    if (!emblaApi) return undefined;
    startAutoplay(selectedIndex);
    return () => stopAutoplay();
  }, [emblaApi, selectedIndex, startAutoplay, stopAutoplay]);

  useEffect(() => {
    if (!emblaApi || !outerRef.current) return undefined;
    const node = outerRef.current;
    let timeoutId: ReturnType<typeof setTimeout>;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            if (entry.isIntersecting) {
              isAutoplayPausedRef.current = false;
              startAutoplay(selectedIndexRef.current);
            } else {
              isAutoplayPausedRef.current = true;
              stopAutoplay();
            }
          }, 150);
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -10% 0px' },
    );

    observer.observe(node);
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [emblaApi, startAutoplay, stopAutoplay]);

  useEffect(() => {
    if (!emblaApi || !outerRef.current) return undefined;
    const node = outerRef.current;

    const onEnter = () => {
      if (!canAutoplay()) return;
      isAutoplayPausedRef.current = true;
      stopAutoplay();
    };
    const onLeave = () => {
      isAutoplayPausedRef.current = false;
      startAutoplay(selectedIndexRef.current);
    };

    node.addEventListener('mouseenter', onEnter);
    node.addEventListener('mouseleave', onLeave);
    return () => {
      node.removeEventListener('mouseenter', onEnter);
      node.removeEventListener('mouseleave', onLeave);
    };
  }, [emblaApi, startAutoplay, stopAutoplay]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const desktop = window.matchMedia(DESKTOP_MQ);
    const motion = window.matchMedia(MOTION_OK_MQ);
    const onChange = () => startAutoplay(selectedIndexRef.current);
    desktop.addEventListener('change', onChange);
    motion.addEventListener('change', onChange);
    return () => {
      desktop.removeEventListener('change', onChange);
      motion.removeEventListener('change', onChange);
    };
  }, [emblaApi, startAutoplay]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onPointerDown = () => stopAutoplay();
    const onPointerUp = () => startAutoplay(selectedIndexRef.current);
    emblaApi.on('pointerDown', onPointerDown).on('pointerUp', onPointerUp);
    return () => {
      emblaApi.off('pointerDown', onPointerDown).off('pointerUp', onPointerUp);
    };
  }, [emblaApi, startAutoplay, stopAutoplay]);

  const gsapCtx = useGsapContext(outerRef);

  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const title = titleRef.current;
      const subtitle = subtitleRef.current;
      const outer = outerRef.current;
      if (!title || !subtitle || !outer) return;

      const copies = Array.from(
        outer.querySelectorAll<HTMLElement>('[data-rm-copy]'),
      );

      const unbindTitle = bindOnceTextReveal(title, {
        ...TEXT_REVEAL_FULL,
        trigger: title,
      });
      const unbindSubtitle = bindOnceTextReveal(subtitle, {
        ...TEXT_REVEAL_SUBTLE,
        delay: 0.08,
        trigger: title,
      });
      const unbindCopies = bindOnceTextReveal(copies, {
        ...TEXT_REVEAL_SUBTLE,
        delay: 0.16,
        stagger: 0.1,
        trigger: title,
      });

      return () => {
        unbindTitle();
        unbindSubtitle();
        unbindCopies();
      };
    });
  }, [gsapCtx, emblaApi]);

  const onTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const dir = event.key === 'ArrowRight' ? 1 : -1;
    const next = (index + dir + CARD_COUNT) % CARD_COUNT;
    scrollTo(next);
    tabRefs.current[next]?.focus();
  };

  const carouselLabel = t('homepage.ringMeasures.aria.carousel');

  return (
    <section
      ref={outerRef}
      className={styles.outer}
      aria-label={ariaLabel ?? t('homepage.ringMeasures.aria.section')}
      data-overlap-layer="measures"
    >
      <div className={styles.content}>
        <div className={styles.head}>
          <div className={styles.copy}>
            <h2
              ref={titleRef}
              className={`${v3.sectionTitle} ${styles.title}`}
            >
              {t('homepage.ringMeasures.title')}
            </h2>
            <p
              ref={subtitleRef}
              className={`${v3.body} ${styles.subtitle}`}
            >
              {t('homepage.ringMeasures.subtitle')}
            </p>
          </div>

          <div
            ref={tabListRef}
            className={styles.tabList}
            role="tablist"
            aria-label={t('homepage.ringMeasures.aria.tablist')}
          >
            <span className={styles.tabHighlight} aria-hidden="true" />
            {cards.map((card, index) => {
              const selected = index === selectedIndex;
              return (
                <button
                  key={card.id}
                  ref={(node) => {
                    tabRefs.current[index] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`ring-measures-tab-${card.id}`}
                  aria-selected={selected}
                  aria-controls={`ring-measures-slide-${card.id}`}
                  tabIndex={selected ? 0 : -1}
                  className={`${v3.pill} ${styles.tab} ${
                    selected ? styles.tabActive : ''
                  }`}
                  onClick={() => scrollTo(index)}
                  onKeyDown={(event) => onTabKeyDown(event, index)}
                >
                  {card.tab}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.carouselRegion}>
          <div
            ref={emblaRef}
            className={styles.viewport}
            role="region"
            aria-roledescription="carousel"
            aria-label={carouselLabel}
          >
            <ul className={styles.track}>
              {cards.map((card, index) => (
                <li
                  key={card.id}
                  className={styles.slide}
                  id={`ring-measures-slide-${card.id}`}
                  role="tabpanel"
                  aria-labelledby={`ring-measures-tab-${card.id}`}
                  aria-roledescription="slide"
                >
                  <div className={styles.media}>
                    <CustomImage
                      className={`${styles.cardImg} ${styles.cardImgDesktop}`}
                      src={card.imageSrc}
                      alt={card.imageAlt}
                      fill
                      sizes="55vh"
                      priority={index === 0}
                      style={{ objectFit: 'cover' }}
                    />
                    <CustomImage
                      className={`${styles.cardImg} ${styles.cardImgMobile}`}
                      src={card.imageSrcMobile}
                      alt={card.imageAlt}
                      fill
                      sizes="40vh"
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                  <div className={styles.cardCopy} data-rm-copy={index}>
                    <h3 className={styles.cardTitle}>{card.title}</h3>
                    <p className={styles.cardDesc}>{card.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={styles.dots} role="group" aria-label={carouselLabel}>
          {cards.map((card, index) => {
            const selected = index === selectedIndex;
            return (
              <button
                key={card.id}
                type="button"
                className={`${styles.dot} ${
                  selected ? styles.dotActive : ''
                }`}
                aria-label={t('homepage.ringMeasures.aria.goToSlide', {
                  name: card.tab,
                })}
                aria-current={selected ? 'true' : undefined}
                onClick={() => scrollTo(index)}
              >
                <span
                  className={styles.dotFill}
                  ref={(node) => {
                    progressRefs.current[index] = node;
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RingMeasuresSection;
